import { useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Input } from '@antimatterhq/ui/components/input';
import { FormikProvider, useFormik } from 'formik';
import { Text } from '@antimatterhq/ui/components/typography';
import { DatasourceUpgradeStringFieldsDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { FormValues } from '@/components/form/values/FormValues';
import { deepEqual } from '@/lib/datasource';

export const DatasourceUpgradeDiffMultiValue = ({
  diff,
  onApply,
  isApplying,
}: {
  diff: DatasourceUpgradeStringFieldsDiff;
  onApply: (value: string[]) => void;
  isApplying?: boolean;
}) => {
  const formik = useFormik({
    initialValues: {
      values: diff.upgradeValue ?? [],
    },
    onSubmit: (values) => {
      onApply(values.values);
    },
  });

  const applied = useMemo(() => {
    return (
      deepEqual(diff.upgradeValue, formik.values.values) &&
      diff.status === 'confirmed'
    );
  }, [diff.upgradeValue, formik.values.values, diff.status]);

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`fieldspec-diff-multivalue-root-${diff.title}`}
    >
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex flex-1 flex-col gap-2">
            {diff.currentValue?.map((value) => (
              <div key={value}>
                <Input
                  value={value}
                  disabled
                  className="mx-auto max-w-[500px]"
                  data-testid="fieldspec-diff-multivalue-current-value"
                />
              </div>
            ))}
            {diff.currentValue?.length === 0 && (
              <div className="mx-auto flex h-full max-w-[500px] items-center justify-center">
                <Text
                  className="text-center italic"
                  color="secondary"
                  data-testid="fieldspec-diff-multivalue-current-value-empty"
                >
                  No previous values
                </Text>
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col items-center">
            <div className="w-[500px]">
              <FormikProvider value={formik}>
                <FormValues
                  values={formik.values.values}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="values"
                  data-testid="fieldspec-diff-multivalue-upgrade-value"
                  maxHeight={200}
                />
              </FormikProvider>
            </div>
          </div>
        </div>
        {applied && (
          <div className="absolute right-4 top-3">
            <Icon
              icon="lucide:check"
              className="text-green-500"
              data-testid="fieldspec-diff-multivalue-accepted-icon"
            />
          </div>
        )}
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          {!!diff.currentValue?.length && (
            <Button
              onClick={() =>
                formik.setFieldValue('values', diff.currentValue ?? [''])
              }
              rightIcon="lucide:chevron-right"
              disabled={
                deepEqual(formik.values.values, diff.currentValue) || isApplying
              }
              data-testid="datasource-upgrade-diff-multivalue-copy-btn"
            >
              Copy to pending
            </Button>
          )}
        </div>
        <div className="flex-1 text-center">
          <Button
            variant="primary"
            onClick={() => onApply(formik.values.values)}
            disabled={applied || isApplying}
            data-testid="datasource-upgrade-diff-multivalue-apply-btn"
          >
            Apply pending version
          </Button>
        </div>
      </div>
    </div>
  );
};
